import { PickupDashboardComponent } from "@/app/infrastructures/dependencies/modules/PickupDashboardComponent";
import store from "@/store";
import { Action, Mutation, getModule, Module, VuexModule } from "vuex-module-decorators";
import { container } from "tsyringe";
import { ListSummaryItem, PickupDashboardMonitoringDetailSummary, RequestListSummary, RequestTabsList } from "@/domain/entities/PickupDashboardSummary";
import { PickupDashboardPresenter } from "@/app/ui/presenters/PickupDashboardPresenter";
import { CourierPickupSummary, PickupDashboardMonitoringCourierSummary, AvailableCourierList, TransferPickupRequest, SenderList, TransferPickupListRequest } from "@/domain/entities/PickupDashboard";
import { CourierSummaryRequest, ShipmentListRequest, PickupShipmentRequest, CourierListRequest, ManualAssignRequest } from "@/data/payload/api/PickupDashboardRequests";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { ExportApiRequest } from "@/data/payload/api/PickupDashboardApiRequest";
import { Pagination } from "@/domain/entities/Pagination";
PickupDashboardComponent.init();

export interface PickupDashboardState {
  courierSummary: CourierPickupSummary;
  availableCouriers: AvailableCourierList;
  senderList: SenderList;
}

@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: "PickupDashboard"
})
class PickupDashboardStore extends VuexModule implements PickupDashboardState {
  courierSummary = new CourierPickupSummary();
  dataSummaryBkdAndCrrcnc = new CourierPickupSummary();
  detailSummary: ListSummaryItem[] =  []
  selectedRowsPickup: ListSummaryItem[] = [];
  pagination = new Pagination(1, 10, 0)
  availableCouriers = new AvailableCourierList();
  senderList = new SenderList();
  loadingCourierSummary = false;
  isSumaryDetailExpanded = true;
  loadingAvailableCouriers = false;
  loadingSenderList = false;
  loadingDetailSummary = false;
  errorCause = "" as string;
  filter = {
    startDate: "",
    endDate: "",
    q: "",
    shipmentType: "",
    courierId: 0 as number | string,
    status: "",
    orderBy: "",
    sortBy: ""
  };

  @Mutation
  public setIsSumaryDetailExpanded(payload: boolean) {
    this.isSumaryDetailExpanded = payload;
  }

  @Mutation
  public setCourierSummary(payload: CourierPickupSummary) {
    this.courierSummary = payload;
  }

  @Mutation
  public setDetailSummaryBkdAndCrrcnc(payload: any) {
    this.dataSummaryBkdAndCrrcnc = payload;
  }

  @Mutation
  public setLoadingCourierSummary(payload: boolean) {
    this.loadingCourierSummary = payload;
  }

  @Mutation
  public setAvailableCouriers(payload: AvailableCourierList) {
    this.availableCouriers = payload;
  }

  @Mutation
  public setLoadingAvailableCouriers(payload: boolean) {
    this.loadingAvailableCouriers = payload;
  }

  @Mutation
  public setSenderList(payload: SenderList) {
    this.senderList = payload;
  }

  @Mutation
  public setLoadingSenderList(payload: boolean) {
    this.loadingSenderList = payload;
  }
  
  @Mutation
  public setLoadingDetailSummary(payload: boolean) {
    this.loadingDetailSummary = payload
  }

  @Mutation
  public setErrorCause(err: any) {
    this.errorCause = err.response ? "server" : "internet"
  }

  @Mutation
  public setDetailSummary(resp: PickupDashboardMonitoringDetailSummary) {
    const page = resp.pagination?.page ?? this.pagination.page;
    const limit = resp.pagination?.limit ?? this.pagination.limit;
    const startIndex = (page - 1) * limit;

    const mapItems = (resp.data || []).map((item, idx) => ({
      ...item,
      incrementId: startIndex + idx + 1,
    }));

    this.detailSummary = mapItems as ListSummaryItem[];
    this.pagination = new Pagination(
      resp.pagination.page,
      resp.pagination.limit,
      resp.pagination.totalData
    );
  }

  @Mutation
  public setFilter(payload: {
    startDate?: string;
    endDate?: string;
    q?: string;
    shipmentType?: string;
    courierId?: number | string;
    status?: string;
    orderBy?: string;
    sortBy?: string;
  }) {
    const entries = Object.entries(payload).filter(([, v]) => v !== undefined);
    if (entries.length === 0) {
      return;
    }
    const next = { ...this.filter, ...Object.fromEntries(entries) };
    const changed = entries.some(([key, value]) => {
      if (key === "courierId") {
        return String(value) !== String(this.filter.courierId);
      }
      return value !== (this.filter as any)[key];
    });
    if (!changed) {
      return;
    }
    this.filter = next;
  }

  @Mutation
  public setTransferPickup(selectedItems: ListSummaryItem[]) {
    this.selectedRowsPickup = selectedItems
  }

  @Mutation
  public setResetstateSummary() {
    this.errorCause = ""
    this.detailSummary = []
    this.selectedRowsPickup = []
  }

  @Mutation
  public setResetFilter() {
    this.filter.q = "",
    this.filter.startDate = "",
    this.filter.endDate = "",
    this.filter.shipmentType = ""
  }

  @Action
  public async geTabsList(params: RequestTabsList) {
    const presenter = container.resolve(PickupDashboardPresenter);
    return presenter.geTabsList(params);
  }

  @Action
  public async getCourierSummary(params: CourierSummaryRequest) {
    const presenter = container.resolve(PickupDashboardPresenter);
    this.setLoadingCourierSummary(true);
    try {
      const res = await presenter.getCourierSummary(params);
      const statusStr = String(params.status || "").toUpperCase();
      const containsExcluded = statusStr.includes("BKD") || statusStr.includes("CRRCNC");
      if (!containsExcluded) {
        this.setCourierSummary(res);
      } else {
        this.setDetailSummaryBkdAndCrrcnc(res);
      }
      return res;
    } finally {
      this.setLoadingCourierSummary(false);
    }
  }

  get crrotw(): PickupDashboardMonitoringCourierSummary {
    const d = this.courierSummary.data.find(i => i.status === "CRROTW");
    return d || new PickupDashboardMonitoringCourierSummary();
  }

  get crrpic(): PickupDashboardMonitoringCourierSummary {
    const d = this.courierSummary.data.find(i => i.status === "CRRPIC");
    return d || new PickupDashboardMonitoringCourierSummary();
  }

  get crrdon(): PickupDashboardMonitoringCourierSummary {
    const d = this.courierSummary.data.find(i => i.status === "CRRDON");
    return d || new PickupDashboardMonitoringCourierSummary();
  }

  get bkd(): PickupDashboardMonitoringCourierSummary {
    const d = this.dataSummaryBkdAndCrrcnc.data.find(i => i.status === "BKD");
    return d || new PickupDashboardMonitoringCourierSummary();
  }

  get crrcnc(): PickupDashboardMonitoringCourierSummary {
    const d = this.dataSummaryBkdAndCrrcnc.data.find(i => i.status === "CRRCNC");
    return d || new PickupDashboardMonitoringCourierSummary();
  }

  get crrnfd(): PickupDashboardMonitoringCourierSummary {
    const d = this.courierSummary.data.find(i => i.status === "CRRNFD");
    return d || new PickupDashboardMonitoringCourierSummary();
  }

  get isLoadingCourierSummary(): boolean {
    return this.loadingCourierSummary;
  }

  get currentFilter() {
    return this.filter;
  }

  @Action
  public async getAvailableCouriers(params: {taskGroupIds?: string, q?: string}) {
    const presenter = container.resolve(PickupDashboardPresenter);
    this.setLoadingAvailableCouriers(true);
    try {
      const res = await presenter.getAvailableCouriers(params.taskGroupIds, params.q);
      this.setAvailableCouriers(res);
      return res;
    } finally {
      this.setLoadingAvailableCouriers(false);
    }
  }

  @Action
  public async transferPickup(payload: TransferPickupRequest) {
    const presenter = container.resolve(PickupDashboardPresenter);
    return presenter.transferPickup(payload);
  }

  @Action
  public async getCourierList(payload: CourierListRequest) {
    const presenter = container.resolve(PickupDashboardPresenter);
    return presenter.getCourierList(payload);
  }

  get isLoadingAvailableCouriers(): boolean {
    return this.loadingAvailableCouriers;
  }

  @Action
  public export(payload: ExportApiRequest) {
    const presenter = container.resolve(PickupDashboardPresenter);
    return presenter.export(payload);
  }

  @Action
  public async getListSummary(params: RequestListSummary) {
    const presenter = container.resolve(PickupDashboardPresenter);
    this.setLoadingDetailSummary(true)
    try {
      const res  = await presenter.getListSummary(params)
      this.setResetstateSummary()
      this.setDetailSummary(res)
    } catch (error) {
      this.setResetstateSummary()
      this.setErrorCause(error)
    } finally {
      this.setLoadingDetailSummary(false)
    }
  }

  @Action
  public getShipmentList(params: ShipmentListRequest): Promise<ResponsePayloadV2> {
    const presenter = container.resolve(PickupDashboardPresenter);
    return presenter.getShipmentList(params);
  }

  @Action
  public getBookingEligibleShipments(params: PickupShipmentRequest) {
    const presenter = container.resolve(PickupDashboardPresenter);
    return presenter.getBookingEligibleShipments(params);
  }

  @Action
  public async getSenderList(params: TransferPickupListRequest) {
    const presenter = container.resolve(PickupDashboardPresenter);
    this.setLoadingSenderList(true);
    try {
      const res = await presenter.getSenderList(params);
      this.setSenderList(res);
      return res;
    } finally {
      this.setLoadingSenderList(false);
    }
  }

  get isLoadingSenderList(): boolean {
    return this.loadingSenderList;
  }

  @Action
  public async manualAssign(payload: ManualAssignRequest) {
    const presenter = container.resolve(PickupDashboardPresenter);
    return presenter.manualAssign(payload);
  }
}

export const PickupDashboardController = getModule(PickupDashboardStore);
